import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { QueueStatus, TodayVisit, TodayVisitsView } from '@wodi/shared';
import { Button, EmptyState, LoadingBlock, Notice, Tag, type TagState } from '@wodi/ui';
import type { ReactNode } from 'react';

import { ApiError } from '../api/client.ts';
import { todayVisitsQuery } from '../api/home.ts';
import { useOnline } from '../hooks/use-online.ts';
import { formatTimeOnly } from '../patients/format.ts';
import styles from './home.module.css';
import { plural } from './setup-items.ts';

// Today's visits at this facility (docs/plans/phase1-home-and-demo-data.md §3): the
// Reception panel and the latest few on Home. Each row opens the patient; the address
// carries the internal id only.

export const TODAY_EMPTY = 'No arrivals yet today. Patients you check in appear here.';

const TYPE_LABELS: Record<TodayVisit['type'], string> = {
  outpatient: 'Outpatient',
  walk_in: 'Walk-in',
  inpatient: 'Inpatient',
  emergency: 'Emergency',
  mortuary: 'Mortuary',
};

const QUEUE_LABELS: Record<QueueStatus, [string, TagState]> = {
  waiting: ['Waiting', 'pending'],
  called: ['Called', 'active'],
  in_service: ['Being seen', 'active'],
  done: ['Done', 'done'],
  transferred_out: ['Moved on', 'done'],
  cancelled: ['Left the queue', 'neutral'],
};

/** Where the visit stands, in a word: the visit's own state once it has ended. */
function stateOf(visit: TodayVisit): [string, TagState] {
  if (visit.status === 'closed') return ['Closed', 'done'];
  if (visit.status === 'cancelled') return ['Cancelled', 'neutral'];
  if (visit.queue === null) return ['Open', 'pending'];
  return QUEUE_LABELS[visit.queue.status];
}

/** "12 arrivals today, 9 still open." Empty when there is nothing to add to the empty state. */
export function todaySummary(view: TodayVisitsView): string {
  const { total, open } = view.counts;
  const earlier =
    view.openFromEarlierDays === 0
      ? ''
      : ` ${plural(view.openFromEarlierDays, 'visit')} from earlier days ${view.openFromEarlierDays === 1 ? 'is' : 'are'} still open.`;
  if (total === 0) return earlier.trim();
  return `${plural(total, 'arrival')} today, ${String(open)} still open.${earlier}`;
}

export function TodayList({ visits, label }: { visits: readonly TodayVisit[]; label: string }) {
  return (
    <ul className={styles.todayList} aria-label={label}>
      {visits.map((visit) => {
        const [word, state] = stateOf(visit);
        return (
          <li key={visit.id}>
            <Link
              className={styles.todayRow}
              to="/patients/$patientId"
              params={{ patientId: visit.patient.id }}
            >
              <span className={styles.todayTime}>{formatTimeOnly(visit.startedAt)}</span>
              <span className={styles.todayWho}>
                <span className={styles.todayName}>{visit.patient.displayName}</span>
                <span className={styles.todayMeta}>
                  <span className={styles.todayNumber}>{visit.patient.patientNumber}</span>
                  <span>{TYPE_LABELS[visit.type]}</span>
                  {visit.queue === null ? null : <span>{visit.queue.servicePointName}</span>}
                </span>
              </span>
              <Tag state={state}>{word}</Tag>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function errorText(error: unknown): string {
  return error instanceof ApiError
    ? error.message
    : "Today's visits couldn't be loaded. Check the connection, then try again.";
}

/**
 * The query's loading, error, offline and empty states around `children`, which renders
 * the loaded list. Offline keeps the last list and says it may be out of date.
 */
export function TodayVisitsState({
  limit,
  children,
}: {
  limit?: number;
  children: (view: TodayVisitsView) => ReactNode;
}) {
  const today = useQuery(todayVisitsQuery(limit));
  const online = useOnline();
  if (today.isPending) return <LoadingBlock label="Loading today's visits" rows={3} />;
  if (today.isError)
    return (
      <Notice
        tone="error"
        action={
          <Button dense onPress={() => void today.refetch()}>
            Try again
          </Button>
        }
      >
        {errorText(today.error)}
      </Notice>
    );
  return (
    <>
      {online ? null : <Notice tone="info">Offline. This list may be out of date.</Notice>}
      {children(today.data)}
    </>
  );
}

/** Reception's right-hand panel: every arrival today, newest first. */
export function TodayPanel() {
  return (
    <section className={styles.todayPanel} aria-labelledby="today-heading">
      <div className={styles.panelHeader}>
        <h2 id="today-heading" className={styles.sectionHeading}>
          Today
        </h2>
      </div>
      <TodayVisitsState limit={200}>
        {(view) => (
          <>
            {todaySummary(view) === '' ? null : (
              <p className={styles.muted}>{todaySummary(view)}</p>
            )}
            {view.items.length === 0 ? (
              <EmptyState>{TODAY_EMPTY}</EmptyState>
            ) : (
              <TodayList visits={view.items} label="Today's arrivals" />
            )}
            {view.nextCursor === null ? null : (
              <p className={styles.muted}>
                Showing the latest {String(view.items.length)}. Search to find anyone else.
              </p>
            )}
          </>
        )}
      </TodayVisitsState>
    </section>
  );
}
